import { AUTH_EVENTS } from "@/constants/amplitude.constants";
import { SIGNUP_TYPES } from "@/shared/constants/signUpConstants";
import { SignupCopiesType } from "@/shared/data/navbar.data";
import Amplitude from "@/shared/services/amplitude.service";
import useSignUpStore from "@/shared/store/useSignUpStore";
import { SIGNUP_FORM_FIELDS } from "@/shared/types/signup.types";
import {
  getCurrentURL,
  getReferrer,
  getScreenWidth,
  objectCopy,
} from "@/shared/utils/commons";
import { onFormInputBlur, onFormInputChange } from "@/shared/utils/signup";
import { getUTMFieldsFromLocalStorage } from "@/shared/utils/utm.util";
import { CountryPhoneCodeInput, INPUT_TYPES, Input } from "@graphy/ui";
import { useRouter } from "next/router";
import { parseCookies } from "nookies";
import React, { useRef } from "react";
import { getCountryCallingCode } from "react-phone-number-input";

const SignupPhoneInput = ({
  signupCopies,
}: {
  signupCopies: SignupCopiesType;
}) => {
  const contactNumberInputRef = useRef<HTMLInputElement>(null);

  const router = useRouter();

  const {
    contactNumber,
    callingCode,
    signUpInputsErrors,
    countryCode,
    setCountryCode,
    setCallingCode,
    setContactNumber,
    setSignUpInputsErrors,
  } = useSignUpStore();
  const cookies = parseCookies();

  const onNumberChange = React.useCallback(
    (value: any) => {
      let updatedSignUpInputsErrors = signUpInputsErrors
        ? objectCopy(signUpInputsErrors)
        : {};
      if (updatedSignUpInputsErrors[SIGNUP_FORM_FIELDS.PHONE])
        delete updatedSignUpInputsErrors[SIGNUP_FORM_FIELDS.PHONE];
      setSignUpInputsErrors(updatedSignUpInputsErrors);
      setContactNumber(value);
    },
    [setContactNumber, setSignUpInputsErrors, signUpInputsErrors]
  );

  const onCountryChange = React.useCallback(
    (value: any) => {
      const callingCode = getCountryCallingCode(value.code as any);
      setCountryCode(value.code);
      setCallingCode(callingCode);
      const {
        current_url,
        screen_width,
        referrer,
        utm_params,
        phone_country,
        signup_type,
      } = AUTH_EVENTS.SIGNUP_PHONE_COUNTRY_SELECTED.PROPERTIES;

      new Amplitude().sendEvent({
        type: AUTH_EVENTS.SIGNUP_PHONE_COUNTRY_SELECTED.LABEL,
        properties: {
          [current_url]: getCurrentURL(),
          [screen_width]: getScreenWidth(),
          [referrer]: getReferrer(),
          [utm_params]: getUTMFieldsFromLocalStorage(),
          [phone_country]: callingCode,
          [signup_type]:
            router.pathname === "/signup"
              ? SIGNUP_TYPES.FULLPAGE
              : SIGNUP_TYPES.MODAL,
        } as { [key: string]: string },
      });

      contactNumberInputRef.current?.focus();
    },
    [router.pathname, setCallingCode, setCountryCode]
  );

  const actionAmplitudeEvents = (key: string) => {
    const {
      current_url,
      screen_width,
      referrer,
      utm_params,
      phone_number,
      signup_type,
    } = AUTH_EVENTS.SIGNUP_PHONE_NUMBER_ENTERED.PROPERTIES;
    new Amplitude().sendEvent({
      type: AUTH_EVENTS.SIGNUP_PHONE_NUMBER_ENTERED.LABEL,
      properties: {
        [current_url]: getCurrentURL(),
        [screen_width]: getScreenWidth(),
        [referrer]: getReferrer(),
        [utm_params]: getUTMFieldsFromLocalStorage(),
        [phone_number]: contactNumber,
        [signup_type]:
          router.pathname === "/signup"
            ? SIGNUP_TYPES.FULLPAGE
            : SIGNUP_TYPES.MODAL,
         signup_version: cookies.signupVersion,
      } as { [key: string]: string },
    });
  };

  return (
    <div id="phone-input-wrapper" className={`flex w-full items-center`}>
     <div className="bg-white !rounded-[12px]">
        <CountryPhoneCodeInput
          key={countryCode}
          countryCode={countryCode}
          phoneInputClassName="h-[44px] flex-1 text-grey-3 !bg-white"
          onSelected={(value) => onCountryChange(value)}
          label=""
          isShortLabel={true}
          borderLight={true}
        />
      </div>

      <Input
        ref={contactNumberInputRef}
        type={INPUT_TYPES.NUMBER}
        containerMargin="ml-4"
        inputPadding="p-[0.65rem]"
        className={`flex-2 h-12  w-full rounded-[12px] border border-grey-3 px-[24px] py-[14px] text-body text-darkSlate01`}
        value={contactNumber}
        placeholder={signupCopies.phoneNumber.placeholder}
        errorMessage={signUpInputsErrors?.[SIGNUP_FORM_FIELDS.PHONE]}
        onChange={(value) => {
          onNumberChange(value);
          onFormInputChange(SIGNUP_FORM_FIELDS.PHONE);
        }}
        onBlur={(value) => {
          actionAmplitudeEvents(SIGNUP_FORM_FIELDS.PHONE);
          onFormInputBlur(value, SIGNUP_FORM_FIELDS.PHONE);
        }}
      />
    </div>
  );
};

export default SignupPhoneInput;
